import React from 'react';
import { Badge, TagPill } from 'components/badges';
import TodoBodyMarkup from '../TodoBodyMarkup/TodoBodyMarkup';
import { TODO_PRIORITY_BADGE_COLORS, TODO_PRIORITY_LABELS, TODO_STATUS_COLUMNS } from 'constants/todos';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';

export default function TodoKanbanCard({ todo, currentStatus, onAction, onMove }) {
  const badgeColor = TODO_PRIORITY_BADGE_COLORS[todo.priority] || 'gray';
  const priorityLabel = TODO_PRIORITY_LABELS[todo.priority] || todo.priority;
  const moveTargets = TODO_STATUS_COLUMNS.filter(col => col.id !== currentStatus);
  const tags = Array.isArray(todo.tags) ? todo.tags : [];

  return (
    <div
      draggable
      data-todo-id={todo._id}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/json', JSON.stringify({
          todoId: todo._id,
          fromStatus: currentStatus,
        }));
      }}
      className="group cursor-grab active:cursor-grabbing rounded-lg border border-gray-700 bg-gray-800 p-3 shadow-sm transition-shadow hover:shadow-md hover:border-gray-600"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex flex-1 items-center gap-2.5">
          <Badge size="sm" color={badgeColor} className="shrink-0">
            {priorityLabel}
          </Badge>
          <p className="min-w-0 flex-1 text-sm font-medium leading-snug text-white truncate">{todo.name}</p>
        </div>
        <Menu as="div" className="relative flex-shrink-0">
          <MenuButton className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white cursor-pointer">
            <EllipsisVerticalIcon className="h-4 w-4" />
          </MenuButton>
          <MenuItems
            transition
            anchor="bottom end"
            className="z-50 w-44 origin-top-right rounded-md bg-gray-800 py-1 shadow-lg ring-1 ring-white/10 focus:outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <MenuItem>
              <button
                onClick={() => onAction('edit', todo)}
                className="block w-full px-3 py-1.5 text-left text-sm text-gray-300 data-[focus]:bg-gray-700 data-[focus]:text-white cursor-pointer"
              >
                Edit
              </button>
            </MenuItem>
            <MenuItem>
              <button
                onClick={() => onAction('attach-to-reminder', todo)}
                className="block w-full px-3 py-1.5 text-left text-sm text-gray-300 data-[focus]:bg-gray-700 data-[focus]:text-white cursor-pointer"
              >
                Attach to Reminder
              </button>
            </MenuItem>
            <div className="my-1 border-t border-gray-700" />
            {moveTargets.map(col => (
              <MenuItem key={col.id}>
                <button
                  onClick={() => onMove(todo._id, col.id)}
                  className="block w-full px-3 py-1.5 text-left text-sm text-gray-300 data-[focus]:bg-gray-700 data-[focus]:text-white cursor-pointer"
                >
                  Move to {col.label}
                </button>
              </MenuItem>
            ))}
            <div className="my-1 border-t border-gray-700" />
            <MenuItem>
              <button
                onClick={() => onAction('delete', todo)}
                className="block w-full px-3 py-1.5 text-left text-sm text-red-400 data-[focus]:bg-gray-700 data-[focus]:text-red-300 cursor-pointer"
              >
                Delete
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>
      {todo.body && (
        <TodoBodyMarkup body={todo.body} compact collapsible className="mt-1.5" />
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => {
          const tagId = typeof tag === 'string' ? tag : (tag._id || tag.name);
          const tagName = typeof tag === 'string' ? tag : tag.name;

          return <TagPill key={tagId}>{tagName}</TagPill>;
        })}
      </div>
      {todo.activeReminderCount > 0 && (
        <div className="mt-2 text-xs text-gray-400">
          Attached to {todo.activeReminderCount} {todo.activeReminderCount === 1 ? 'reminder' : 'reminders'}
        </div>
      )}
    </div>
  );
}
